/**
 * Client-side unread tracking for the alerts feed.
 *
 * There is NO server-side read/ack state in v1 (spec §10/§11): unread is a
 * single watermark — the newest `fired_at` the user has looked at — kept in
 * localStorage. localStorage rather than sessionStorage on purpose: this is
 * non-sensitive UI state that should survive closing the tab (tokens stay in
 * sessionStorage). A cleared browser simply shows everything as unread again.
 */

export const ALERTS_SEEN_KEY = 'watchtower.alerts.lastSeen';

/** The newest `fired_at` the user has seen, or '' when they have seen none. */
export function readLastSeen(): string {
  try {
    return localStorage.getItem(ALERTS_SEEN_KEY) ?? '';
  } catch {
    // Storage disabled (private mode / blocked) — degrade to "all unread".
    return '';
  }
}

/** Advance the watermark. Monotonic: never moves backwards, ignores a blank. */
export function writeLastSeen(firedAt: string): void {
  if (!firedAt) return;
  try {
    if (firedAt > readLastSeen()) localStorage.setItem(ALERTS_SEEN_KEY, firedAt);
  } catch {
    /* storage unavailable — unread state simply does not persist */
  }
}

/** The maximum `fired_at` in a feed, order-independent. */
export function newestFiredAt(alerts: Array<{ fired_at: string }>): string {
  return alerts.reduce((max, a) => (a.fired_at > max ? a.fired_at : max), '');
}

/** Alerts strictly newer than the watermark. */
export function unreadCount(alerts: Array<{ fired_at: string }>, lastSeen: string): number {
  return alerts.filter((a) => a.fired_at > lastSeen).length;
}
